import React from 'react';

// Simple markdown renderer for chat messages
const MarkdownRenderer = ({ content, className = '' }) => {
  const renderMarkdown = (text) => {
    if (!text) return null;
    
    // Split by lines to handle block elements
    const lines = text.split('\n');
    const elements = [];
    let currentParagraph = [];
    let inList = false;
    let listItems = [];
    
    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        elements.push(
          <p key={elements.length} className="mb-3 last:mb-0">
            {currentParagraph.map((item, i) => (
              <React.Fragment key={i}>{item}</React.Fragment>
            ))}
          </p>
        );
        currentParagraph = [];
      }
    };
    
    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={elements.length} className="mb-3 ml-4 space-y-1">
            {listItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-crimson mt-1.5 text-xs">●</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };
    
    const processInlineMarkdown = (line) => {
      // Process bold text **text** or __text__
      let result = [];
      let remaining = line;
      let key = 0;
      
      const boldRegex = /\*\*(.+?)\*\*|__(.+?)__/g;
      let lastIndex = 0;
      let match;
      
      while ((match = boldRegex.exec(line)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          result.push(line.slice(lastIndex, match.index));
        }
        // Add the bold text
        const boldText = match[1] || match[2];
        result.push(
          <strong key={key++} className="font-semibold text-crimson">
            {boldText}
          </strong>
        );
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text
      if (lastIndex < line.length) {
        result.push(line.slice(lastIndex));
      }
      
      return result.length > 0 ? result : [line];
    };
    
    lines.forEach((line, lineIndex) => {
      const trimmedLine = line.trim();
      
      // Handle headers (### Header)
      if (trimmedLine.startsWith('###')) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.replace(/^#+\s*/, '');
        elements.push(
          <h3 key={elements.length} className="font-heading font-bold text-lg text-crimson mt-4 mb-2 uppercase tracking-wide">
            {headerText}
          </h3>
        );
        return;
      }
      
      if (trimmedLine.startsWith('##')) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.replace(/^#+\s*/, '');
        elements.push(
          <h2 key={elements.length} className="font-heading font-bold text-xl text-crimson mt-4 mb-2 uppercase tracking-wide">
            {headerText}
          </h2>
        );
        return;
      }
      
      // Handle horizontal rules (--- or ___)
      if (/^[-_]{3,}$/.test(trimmedLine)) {
        flushParagraph();
        flushList();
        elements.push(
          <hr key={elements.length} className="border-mono-line my-3" />
        );
        return;
      }
      
      // Handle bullet points (* item, - item, • item)
      if (/^[\*\-•]\s/.test(trimmedLine)) {
        flushParagraph();
        if (!inList) {
          inList = true;
        }
        const itemText = trimmedLine.replace(/^[\*\-•]\s*/, '');
        listItems.push(processInlineMarkdown(itemText));
        return;
      }
      
      // Handle numbered lists (1. item)
      if (/^\d+\.\s/.test(trimmedLine)) {
        flushParagraph();
        flushList();
        const itemText = trimmedLine.replace(/^\d+\.\s*/, '');
        elements.push(
          <div key={elements.length} className="flex items-start gap-2 mb-2">
            <span className="text-crimson font-bold min-w-[1.5rem]">
              {trimmedLine.match(/^\d+/)[0]}.
            </span>
            <span>{processInlineMarkdown(itemText)}</span>
          </div>
        );
        return;
      }
      
      // End list if we hit a non-list item
      if (inList && trimmedLine && !/^[\*\-•]\s/.test(trimmedLine)) {
        flushList();
      }
      
      // Handle empty lines
      if (!trimmedLine) {
        flushParagraph();
        flushList();
        return;
      }
      
      // Regular text - add to current paragraph
      const processedLine = processInlineMarkdown(trimmedLine);
      currentParagraph.push(...processedLine);
      currentParagraph.push(' ');
    });
    
    // Flush any remaining content
    flushParagraph();
    flushList();
    
    return elements;
  };
  
  return (
    <div className={`markdown-content ${className}`}>
      {renderMarkdown(content)}
    </div>
  );
};

export default MarkdownRenderer;
